import { LinearProgress, styled } from "@mui/material";
import { GridCellParams } from "@mui/x-data-grid-premium";
import { useFeatureGate } from "@statsig/react-bindings";
import { keepPreviousData } from "@tanstack/react-query";
import { includes, lowerCase } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { Box } from "@/Components/v2/atoms/Box";
import { OutlinedButton, PrimaryButton } from "@/Components/v2/atoms/Button";
import { CircularProgress } from "@/Components/v2/atoms/Progress/CircularProgress";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { DataGrid } from "@/Components/v2/organism/DataGrid";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ALL_GEOGRAPHIES_LABEL } from "@/pages/scenario-builder/constants";
import { ScenarioFilterTypeEnum, StatusEnum } from "@/pages/scenario-builder/types";
import { useDeleteScenario } from "@/pages/scenario-builder/v3/api/deleteScenario";
import { useScenarios } from "@/pages/scenario-builder/v3/api/getScenarios";
import { useScenariosV2 } from "@/pages/scenario-builder/v3/api/v2/getScenarios";
import { Layer } from "@/Utils/hierarchy";

import { useDuplicateScenario } from "../../api/duplicateScenario";
import { confirmDeleteScenario } from "../../components/delete-confirmation/DeleteConfirmationModal";
import ScenarioBuilderHeader from "../../components/ScenarioBuilderHeader";
import { useGeoEnabled } from "../../hooks/useGeoEnabled";
import { updateStatusFilter } from "../../utils/scenarioFilters";
import { ScenarioType, ScenarioTypeEnum } from "../scenario-form/types";
import { ScenarioToolBar } from "./ScenarioToolBar";
import { ColumnField, TableRow, useScenariosPageColumns } from "./useScenariosPageColumns";
import NewScenarioModalPage from "../new-scenario-modal/NewScenarioModalPage";
import SpiralBackground from "@/pages/scenario-builder/images/SpiralBackground.svg";
import NoScenarioCTAImage from "@/pages/scenario-builder/images/NoScenarioCTA.png";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-root": {
    border: "none",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.tableColumnHeader.background,
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader:last-child": {
    paddingRight: "0px",
  },
  "& .MuiDataGrid-cell:last-child": {
    paddingRight: "0px",
  },
}));

// Initial state values
const ORDERED_STATUS = [
  StatusEnum.Draft,
  StatusEnum.Completed,
  StatusEnum.Approved,
  StatusEnum.Processing,
  StatusEnum.Archived,
  StatusEnum.Error,
] as const;

const initialStatusFilters = ORDERED_STATUS.slice();
const initialFilterTypes = Object.values(ScenarioFilterTypeEnum);
const initialSelectedColumns: ColumnField[] = [
  "scenarioName",
  "scope",
  "type",
  "status",
  "simulatedROI",
  "createdBy",
  "createdOn",
  "geography",
];

export const NoScenariosResetPrompt = ({ onReset }: { onReset: () => void }) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "15rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack spacing={2} alignItems="center">
        <Typography variant="body1" fontWeight={600}>
          No scenarios found. Reset your filters and try again.
        </Typography>
        <OutlinedButton variant="outlined" onClick={onReset}>
          Reset Filters
        </OutlinedButton>
      </Stack>
    </Box>
  );
};

export default NoScenariosResetPrompt;

export const NoScenariosCTA = ({
  handleCreateNewScenario,
}: {
  handleCreateNewScenario: () => void;
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${SpiralBackground})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "1440px",
        backgroundPosition: "bottom right",
        pt: "120px",
      }}
    >
      <Stack spacing={2} direction="column" sx={{ width: "600px" }}>
        <Stack direction="row" justifyContent="center">
          <img src={NoScenarioCTAImage} alt="Welcome to Scenario Builder" />
        </Stack>
        <Typography variant="h3" fontWeight={600} fontSize="1.5rem" textAlign="center">
          Welcome to Scenario Builder
        </Typography>
        <Typography variant="body1" textAlign="center">
          Let&apos;s make better growth decisions. Create your first Scenario to optimize your media
          spend and convert it into an Action Plan.
        </Typography>
        <Stack direction="row" justifyContent="center">
          <PrimaryButton onClick={handleCreateNewScenario}>Create Scenario</PrimaryButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export const ScenariosPage = () => {
  const isGeoEnabled = useGeoEnabled();

  const history = useHistory();
  const location = useLocation();

  const { scenarioCreated, moduleOpened } = useAnalytics();

  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);

  // Local state instead of Zustand
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<{
    status: StatusEnum[];
    type: ScenarioFilterTypeEnum[];
  }>({
    status: initialStatusFilters,
    type: initialFilterTypes,
  });

  const [customBuildOpen, setCustomBuildOpen] = useState(false);

  const handleCloseCustomBuild = () => {
    setCustomBuildOpen(false);
  };
  const handleCreateNewScenario = () => {
    setCustomBuildOpen(true);
  };

  const combinedFilters = useMemo(() => {
    return {
      ...filters,
      searchTerm,
    };
  }, [filters, searchTerm]);

  const [toggleColumns, setToggleColumns] = useState<Record<ColumnField, boolean>>(() => {
    const allColumns = initialSelectedColumns;
    const columnMap: Record<ColumnField, boolean> = {} as Record<ColumnField, boolean>;
    allColumns.forEach((column) => {
      columnMap[column] = true;
    });
    return columnMap;
  });

  const {
    data: scenariosV1 = [],
    isFetching: isFetchingScenariosV1,
    refetch: refetchScenariosV1,
  } = useScenarios({
    filters: {
      scenarioStatus: filters.status,
      scenarioType: filters.type,
      name: searchTerm,
    },
    config: {
      // So we don't snap between no rows and some rows when query has resolved
      placeholderData: keepPreviousData,
      enabled: !isScenariosApiEnabled,
    },
  });

  const {
    data: scenariosV2 = [],
    isFetching: isFetchingScenariosV2,
    refetch: refetchScenariosV2,
  } = useScenariosV2({
    config: {
      enabled: isScenariosApiEnabled,
    },
  });

  const filteredScenariosV2 = useMemo(() => {
    return scenariosV2.filter((scenario) => {
      return (
        includes(lowerCase(scenario.name), lowerCase(searchTerm)) &&
        filters.status.includes(scenario.status) &&
        filters.type.includes(
          (() => {
            const scenarioType = scenario.type as any;
            if (scenarioType === "budget") return ScenarioFilterTypeEnum.Forecast;
            if (scenarioType === "optimizer") return ScenarioFilterTypeEnum.Optimizer;
            return scenarioType;
          })()
        )
      );
    });
  }, [scenariosV2, searchTerm, filters]);

  const scenarios = isScenariosApiEnabled ? filteredScenariosV2 : scenariosV1;
  const isFetchingScenarios = isScenariosApiEnabled ? isFetchingScenariosV2 : isFetchingScenariosV1;
  const refetchScenarios = isScenariosApiEnabled ? refetchScenariosV2 : refetchScenariosV1;
  const showCTA =
    scenarios.length === 0 &&
    !isFetchingScenarios &&
    filters.status.length === initialStatusFilters.length &&
    filters.type.length === initialFilterTypes.length &&
    searchTerm === "";

  // Convert toggleColumns back to selectedColumns array for compatibility
  const selectedColumns = useMemo(() => {
    return Object.entries(toggleColumns)
      .filter(([_, isVisible]) => isVisible)
      .map(([column]) => column as ColumnField);
  }, [toggleColumns]);

  const handleColumnVisibilityChange = (column: ColumnField, checked: boolean) => {
    setToggleColumns((prev) => ({
      ...prev,
      [column]: checked,
    }));
  };

  const handleScenarioStatusChange = (value: StatusEnum, checked: boolean) => {
    const newFilters = updateStatusFilter(filters.status, value, checked);
    setFilters((prev) => ({
      ...prev,
      status: newFilters,
    }));
  };

  const handleScenarioTypeChange = (value: ScenarioFilterTypeEnum, checked: boolean) => {
    const newTypeFilters = checked
      ? [...filters.type, value]
      : filters.type.filter((type: ScenarioFilterTypeEnum) => type !== value);
    setFilters((prev) => ({
      ...prev,
      type: newTypeFilters,
    }));
  };

  const handleScenarioSearch = (term: string) => {
    setSearchTerm(term);
    setFilters((prev) => ({
      ...prev,
      searchTerm: term,
    }));
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilters({
      status: initialStatusFilters,
      type: initialFilterTypes,
    });
  };

  const duplicateScenario = useDuplicateScenario({
    isScenariosApiEnabled,
    config: {
      onSuccess: (data) => {
        toast.success(`"${data.name}" duplicated`);
        scenarioCreated({
          scenarioType: ScenarioType.toString(
            data.scenarioType as ScenarioTypeEnum,
            data.scenarioCount || undefined
          ),
          scenarioId: data.uuid,
          scenarioName: data.name,
          isCloned: true,
        });
        history.push(`/scenarios-v3/${data.uuid}/form/`);
      },
      onError: (data) => {
        toast.error(`"${data.name}" failed to duplicate. Please try again.`);
      },
    },
  });

  const handleClick = (e: GridCellParams<TableRow>) => {
    if (e.field === "contextMenu") {
      return;
    }
    if (e.row.status === StatusEnum.Draft) {
      history.push(`/scenarios-v3/${e.row.scenarioId}/form`);
    } else {
      history.push(`/scenarios-v3/${e.row.scenarioId}/results`, {
        background: location,
      });
    }
  };

  const deleteScenarioMutation = useDeleteScenario({ isScenariosApiEnabled });

  const handleDelete = async (scenarioId: string) => {
    const selectedScenario = scenarios.find((scenario) => scenario.uuid === scenarioId);

    if (selectedScenario) {
      const confirmed = await confirmDeleteScenario({
        scenarioName: selectedScenario.name,
      });
      if (confirmed) {
        deleteScenarioMutation.mutateAsync(scenarioId);
      }
    }
  };

  const handleDuplicate = (scenarioId: string) => {
    duplicateScenario.mutate(scenarioId);
  };

  useEffect(() => {
    moduleOpened("Scenario Builder", Layer.Portfolio);
  }, []);

  const rows = useMemo(() => {
    const isAllGeosEnabled = isGeoEnabled;

    return scenarios.map((scenario) => {
      // In V2 mode (always enabled), treat Super Optimizer as Optimizer for the type field
      const scenarioTypeEnum = ScenarioType.isSuperOptimizer(scenario.type, scenario.scenarioCount)
        ? ScenarioTypeEnum.Optimizer
        : scenario.type;

      const typeDisplayName = ScenarioType.getDisplayName(scenario.type, scenario.scenarioCount);

      return {
        id: scenario.uuid,
        scenarioId: scenario.uuid,
        scenarioName: scenario.name,
        type: scenarioTypeEnum,
        typeDisplayName,
        startDate: scenario.startDate,
        endDate: scenario.endDate,
        createdBy: scenario.createdBy,
        createdOn: scenario.createdAt,
        simulatedSpend: scenario.spend,
        simulatedRevenue: scenario.revenue,
        simulatedROI: scenario.roi,
        status: scenario.status,
        geography: isAllGeosEnabled
          ? (scenario.geography ?? ALL_GEOGRAPHIES_LABEL)
          : scenario.geography,
        statusMessage: scenario.statusMessage,
        targetNodeUUID: scenario.targetNodeUUID,
        brand: scenario.brand,
        scenarioCount: scenario.scenarioCount,
      };
    });
  }, [scenarios, isGeoEnabled]);

  const allColumns = useScenariosPageColumns({
    onDelete: handleDelete,
    onDuplicate: handleDuplicate,
    isGeoEnabled: !isGeoEnabled.isLoading && isGeoEnabled.isEnabled,
  });

  const columns = useMemo(() => {
    const filteredColumns = allColumns.filter((column) => {
      const field = column.field;
      if (field === "contextMenu") return true;
      if (selectedColumns) {
        return selectedColumns.includes(field as ColumnField);
      }
      return true;
    });

    return filteredColumns;
  }, [allColumns, selectedColumns]);

  const isLoading = isGeoEnabled.isLoading;

  if (isLoading) {
    return <CircularProgress size={150} sx={{ marginTop: "30rem" }} />;
  }

  return (
    <Box>
      <Stack spacing={2} mb={2} paddingX={4} paddingTop={2} paddingBottom={2}>
        {showCTA && <NoScenariosCTA handleCreateNewScenario={handleCreateNewScenario} />}
        {!showCTA && (
          <>
            <ScenarioBuilderHeader refetchScenarios={refetchScenarios} />
            <Stack>
              <ScenarioToolBar
                onStatusChange={handleScenarioStatusChange}
                onTypeChange={handleScenarioTypeChange}
                onSearchChange={handleScenarioSearch}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                filters={combinedFilters}
                selectedColumns={selectedColumns}
                isGeoEnabled={isGeoEnabled.isEnabled}
              />
            </Stack>
            <StyledDataGrid
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableMultipleRowSelection
              onCellClick={handleClick}
              rows={rows}
              columns={columns}
              pagination
              pageSizeOptions={[15, 30, 50]}
              autosizeOnMount
              autosizeOptions={{
                columns: ["scenarioName", "scope", "type", "status", "simulatedROI"],
                includeOutliers: true,
                includeHeaders: true,
                expand: true,
              }}
              density="compact"
              loading={isFetchingScenarios}
              slots={{
                noRowsOverlay: () => <NoScenariosResetPrompt onReset={handleResetFilters} />,
                loadingOverlay: () => <LinearProgress />,
              }}
              sx={{
                "& .MuiDataGrid-overlayWrapper": {
                  height: "auto !important",
                },
                "& .MuiDataGrid-overlayWrapperInner": {
                  height: "auto !important",
                },
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 15 },
                },
                sorting: {
                  sortModel: [
                    {
                      field: "createdOn",
                      sort: "desc",
                    },
                  ],
                },
              }}
            />
          </>
        )}
      </Stack>
      <NewScenarioModalPage open={customBuildOpen} handleClose={handleCloseCustomBuild} />
    </Box>
  );
};
